import React, { useState } from 'react';
import { Alert, Badge, Button, Card, Form, Input, Popconfirm, Select, Space, Table, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { RichTextEditor } from '../../components/common/RichTextEditor';
import {
  RecipientSourceForm,
  type RecipientSourceState,
} from '../../components/admin/bulk-email/RecipientSourceForm';
import {
  createCampaign,
  finalizeCampaign,
  listCampaigns,
  resumeCampaign,
  type CampaignSummary,
  type CreateCampaignPayload,
} from '../../services/bulk-email.service';
import { CAMPAIGN_SOURCE_LABELS, CAMPAIGN_STATUS_LABELS } from '../../constants/bulk-email.constants';

const { Title, Paragraph } = Typography;

export const BulkEmail: React.FC = () => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [contentHtml, setContentHtml] = useState('');
  const [recipientState, setRecipientState] = useState<RecipientSourceState>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hasValidRecipients =
    !!recipientState && (recipientState.recipientCount ?? 0) > 0;
  const canSubmit =
    hasValidRecipients && subject.trim().length > 0 && contentHtml.trim().length > 0 && !submitting;

  const [historyPage, setHistoryPage] = useState(1);
  const [historyStatus, setHistoryStatus] = useState<string | undefined>(undefined);
  const [resumingId, setResumingId] = useState<number | null>(null);
  const [finalizingId, setFinalizingId] = useState<number | null>(null);

  const {
    data: history,
    isLoading: historyLoading,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: ['bulk-email-campaigns', historyPage, historyStatus],
    queryFn: () => listCampaigns({ page: historyPage, pageSize: 10, status: historyStatus }),
  });

  const historyColumns = [
    {
      title: 'Assunto',
      dataIndex: 'subject',
      key: 'subject',
      render: (subject: string, record: CampaignSummary) => (
        <Link to={`/admin/bulk-email/campaigns/${record.id}`}>{subject}</Link>
      ),
    },
    {
      title: 'Origem',
      dataIndex: 'source',
      key: 'source',
      render: (source: string) => CAMPAIGN_SOURCE_LABELS[source] ?? source,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const info = CAMPAIGN_STATUS_LABELS[status] ?? { label: status, color: 'default' };
        return <Badge status={info.color as any} text={info.label} />;
      },
    },
    {
      title: 'Destinatários',
      key: 'recipients',
      render: (_: unknown, record: CampaignSummary) =>
        `${record.sentCount}/${record.totalRecipients}${record.failedCount ? ` (${record.failedCount} falhas)` : ''}`,
    },
    {
      title: 'Disparado por',
      dataIndex: 'adminUserName',
      key: 'adminUserName',
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => new Date(createdAt).toLocaleString('pt-BR'),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_: unknown, record: CampaignSummary) => {
        if (!record.canResume) return null;
        const anyBusy = resumingId !== null || finalizingId !== null;
        const disabled = anyBusy && resumingId !== record.id && finalizingId !== record.id;
        return (
          <Space size="small">
            <Button
              size="small"
              loading={resumingId === record.id}
              disabled={disabled}
              onClick={() => handleResume(record.id)}
            >
              Retomar processamento
            </Button>
            <Popconfirm
              title="Finalizar campanha"
              description="A campanha será marcada como concluída com falhas e os destinatários pendentes não serão mais processados. Deseja continuar?"
              okText="Finalizar"
              cancelText="Cancelar"
              onConfirm={() => handleFinalize(record.id)}
            >
              <Button size="small" danger loading={finalizingId === record.id} disabled={disabled}>
                Finalizar
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  const handleResume = async (campaignId: number) => {
    setResumingId(campaignId);
    try {
      await resumeCampaign(campaignId);
      message.success('Processamento retomado.');
      await refetchHistory();
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Erro ao retomar o processamento da campanha');
      message.error(errorMsg);
    } finally {
      setResumingId(null);
    }
  };

  const handleFinalize = async (campaignId: number) => {
    setFinalizingId(campaignId);
    try {
      await finalizeCampaign(campaignId);
      message.success('Campanha finalizada.');
      await refetchHistory();
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Erro ao finalizar a campanha');
      message.error(errorMsg);
    } finally {
      setFinalizingId(null);
    }
  };

  const handleSubmit = async () => {
    if (!recipientState) return;
    setSubmitting(true);
    setError(null);
    try {
      let payload: CreateCampaignPayload;
      if (recipientState.source === 'FILTERS') {
        payload = { source: 'FILTERS', subject, contentHtml, ...recipientState.criteria };
      } else if (recipientState.source === 'SURVEY') {
        if (!recipientState.surveyId) throw new Error('Selecione uma pesquisa.');
        payload = { source: 'SURVEY', subject, contentHtml, surveyId: recipientState.surveyId };
      } else {
        if (!recipientState.file) throw new Error('Faça upload de um arquivo JSON.');
        payload = { source: 'JSON', subject, contentHtml, file: recipientState.file };
      }

      const result = await createCampaign(payload);
      message.success('Campanha disparada com sucesso.');
      navigate(`/admin/bulk-email/campaigns/${result.campaignId}`);
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.error || (err instanceof Error ? err.message : 'Erro ao disparar campanha');
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-bulk-email-page">
      <Title level={2}>Comunicação em Massa</Title>
      <Paragraph type="secondary">
        Envie um email para um segmento de usuários, participantes de uma pesquisa finalizada, ou uma
        lista carregada via JSON.
      </Paragraph>

      {error && (
        <Alert
          type="error"
          message={error}
          style={{ marginBottom: 16 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Card title="1. Destinatários" style={{ marginBottom: 16 }}>
        <RecipientSourceForm onChange={setRecipientState} />
      </Card>

      <Card title="2. Conteúdo do email" style={{ marginBottom: 16 }}>
        <Form layout="vertical">
          <Form.Item label="Assunto" required>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Ex.: Novidades na PePoll para você, {{nomePreferido}}"
              maxLength={255}
            />
          </Form.Item>
          <Form.Item label="Conteúdo" required>
            <RichTextEditor
              value={contentHtml}
              onChange={setContentHtml}
              placeholder="Escreva o conteúdo do email..."
            />
          </Form.Item>
        </Form>
      </Card>

      <Button type="primary" size="large" disabled={!canSubmit} loading={submitting} onClick={handleSubmit}>
        Disparar Campanha
      </Button>

      <Card
        title="Histórico de Campanhas"
        style={{ marginTop: 32 }}
        extra={
          <Space>
            <Select
              allowClear
              placeholder="Filtrar por status"
              style={{ width: 220 }}
              value={historyStatus}
              onChange={(value) => {
                setHistoryStatus(value);
                setHistoryPage(1);
              }}
              options={Object.entries(CAMPAIGN_STATUS_LABELS).map(([value, { label }]) => ({
                value,
                label,
              }))}
            />
            <Button icon={<ReloadOutlined />} onClick={() => refetchHistory()} />
          </Space>
        }
      >
        <Table<CampaignSummary>
          rowKey="id"
          loading={historyLoading}
          columns={historyColumns}
          dataSource={history?.items ?? []}
          pagination={{
            current: historyPage,
            pageSize: 10,
            total: history?.totalItems ?? 0,
            onChange: setHistoryPage,
          }}
        />
      </Card>
    </div>
  );
};
